import apiClient from '@/shared/api/client';
import type { ApiResponse } from '@/shared/api/types';
import type { CaptureQuestionSet, ReviewAssistAnswers } from '../captureQuestions';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface CaptureSummary {
  sent30d: number;
  opened: number;
  completed: number;
  completionRate: number | null;
}

export interface MintLinkResult {
  shortCode: string;
  url: string;
}

export interface InvitePerson {
  name: string;
  roleLabel: string;
}

export interface InviteResolution {
  practiceName: string;
  locationCity: string | null;
  logoUrl: string | null;
  logoOnDark: boolean;
  gbpDeepLink: string | null;
  /** What `gbpDeepLink` opens (backend `googleReviewLink`): the review box, the Maps listing, or a Maps search. */
  googleLinkKind?: 'write_review' | 'maps_profile' | 'maps_search';
  people: InvitePerson[];
  // Vertical/specialty-aware question sets for steps 1–3. Optional so a newer
  // client degrades to the legacy dental set against an older backend.
  questions?: CaptureQuestionSet;
  /** Staff test session (?test=1) — excluded from analytics; badge shown. */
  isTest?: boolean;
  /** Text channel: the invite came from Betty's text conversation. */
  via?: 'sms' | null;
  /** The draft the patient approved by text (null on the "just the link" path). */
  approvedDraft?: string | null;
  /** The patient finished in the text thread: open on the Share screen, not the stars. */
  handoffReady?: boolean;
}

export interface PatientReviewItem {
  id: string;
  channel: 'private' | 'google';
  // 'pending' Google submissions (tapped through but never confirmed) are
  // filtered out server-side — practice-facing lists only ever see 'confirmed'.
  status: 'pending' | 'confirmed';
  body: string;
  rating: number | null;
  patientName: string | null;
  patientContact: string | null;
  createdAt: string;
}

export interface CaptureBranding {
  practiceName: string;
  locationCity: string | null;
  logoUrl: string | null;
  logoOnDark: boolean;
  googleReviewQrUrl: string | null;
  /** Public link segment — /review/start/<reviewSlug>. */
  reviewSlug: string;
}

// ─── Admin API (authenticated) ────────────────────────────────────────────────

const adminBase = (locationId: string) => `/locations/${locationId}/capture`;

export const captureAdminApi = {
  getSummary: (locationId: string) =>
    apiClient
      .get<ApiResponse<CaptureSummary>>(`${adminBase(locationId)}/summary`)
      .then((r) => r.data),

  listFeedback: (locationId: string) =>
    apiClient
      .get<ApiResponse<PatientReviewItem[]>>(`${adminBase(locationId)}/feedback`)
      .then((r) => r.data),

  listGoogleSubmissions: (locationId: string) =>
    apiClient
      .get<ApiResponse<PatientReviewItem[]>>(`${adminBase(locationId)}/google`)
      .then((r) => r.data),

  getBranding: (locationId: string) =>
    apiClient
      .get<ApiResponse<CaptureBranding>>(`${adminBase(locationId)}/branding`)
      .then((r) => r.data.data),
};

// ─── Public API (no auth — patient-facing) ────────────────────────────────────

const publicBase = (shortCode: string) => `/capture/${shortCode}`;

/**
 * Steps the wizard reports for PN-internal funnel analytics. The server records
 * scan / open / posted / private itself, so those aren't in this union.
 */
export type CaptureFunnelStep =
  | 'rating'
  | 'view_people'
  | 'people'
  | 'view_reason'
  | 'reason'
  | 'view_standout'
  | 'standout'
  | 'view_detail'
  | 'detail'
  | 'view_own_words'
  | 'own_words'
  | 'view_expectation'
  | 'expectation'
  | 'draft';

export const capturePublicApi = {
  // locationRef is the practice's review slug; location ids still resolve so
  // QR codes printed before slugs existed keep working.
  // source is the code's ?src= placement tag (e.g. business_card), recorded on the invite.
  start: (locationRef: string, opts?: { test?: boolean; source?: string }) =>
    apiClient
      .get<ApiResponse<MintLinkResult>>(`/capture/start/${locationRef}`, {
        params: {
          ...(opts?.test ? { test: '1' } : {}),
          ...(opts?.source ? { src: opts.source } : {}),
        },
      })
      .then((r) => r.data),

  resolve: (shortCode: string) =>
    apiClient.get<ApiResponse<InviteResolution>>(publicBase(shortCode)).then((r) => r.data),

  trackOpen: (shortCode: string) =>
    apiClient.post<ApiResponse<null>>(`${publicBase(shortCode)}/open`).then((r) => r.data),

  trackStep: (shortCode: string, step: CaptureFunnelStep, rating?: number) =>
    apiClient
      .post<ApiResponse<null>>(`${publicBase(shortCode)}/step`, { step, rating })
      .then((r) => r.data),

  assembleAssist: (shortCode: string, answers: ReviewAssistAnswers) =>
    apiClient
      .post<ApiResponse<{ draft: string }>>(`${publicBase(shortCode)}/assist`, answers)
      .then((r) => r.data),

  submitPrivate: (
    shortCode: string,
    body: string,
    rating?: number | null,
    patientName?: string,
    patientContact?: string,
  ) =>
    apiClient
      .post<
        ApiResponse<null>
      >(`${publicBase(shortCode)}/private`, { body, rating, patientName, patientContact })
      .then((r) => r.data),

  // Records the hand-off to Google. The submission stays pending — and stays
  // out of the practice's list — until confirmPosted. Below 5 stars, the
  // backend requires patientName/patientContact (same fields the private-
  // feedback form collects) — see capture.controller.ts markCompleteSchema.
  markComplete: (
    shortCode: string,
    body: string,
    rating?: number | null,
    patientName?: string,
    patientContact?: string,
  ) =>
    apiClient
      .post<
        ApiResponse<null>
      >(`${publicBase(shortCode)}/complete`, { body, rating, patientName, patientContact })
      .then((r) => r.data),

  /** The text hand-off link (/g/:shortCode): records the tap; answers with the Google link. */
  openGoogle: (shortCode: string) =>
    apiClient
      .post<ApiResponse<{ url: string; kind: string }>>(`${publicBase(shortCode)}/google`)
      .then((r) => r.data.data),

  confirmPosted: (shortCode: string) =>
    apiClient
      .post<ApiResponse<null>>(`${publicBase(shortCode)}/confirm-posted`)
      .then((r) => r.data),
};
